import { Scheduling } from '@src/core/buyerApiClient';

const priceKey = (time: string | null, tariffId: string) => `${time}::${tariffId}`;

type PriceMaps = {
  unitPriceByKey: Map<string, number>;
  tariffPriceById: Map<string, { priceCents: number; oldPriceCents: number | null }>;
};

function buildPriceMaps(s: Scheduling): PriceMaps {
  const unitPriceByKey = new Map<string, number>();
  for (const tsup of s.tariffScheduleUnitPrices) {
    for (const t of tsup.tariffs) {
      unitPriceByKey.set(priceKey(tsup.time, t.tariffId), t.priceCents);
    }
  }
  const tariffPriceById = new Map(
    s.tariffGroup.tariffs.map((t) => [t.id, { priceCents: t.priceCents, oldPriceCents: t.oldPriceCents }]),
  );
  return { unitPriceByKey, tariffPriceById };
}

/**
 * Returns a copy of `baseline` with the recalculated prices overlaid from `repriced`, matched by
 * `scheduling.id`. Overrides:
 *  - `tariffScheduleUnitPrices[].tariffs[].priceCents` by `time::tariffId` (current price — source of totals);
 *  - `tariffGroup.tariffs[].{priceCents,oldPriceCents}` by `tariff.id` (from/to price shown on the rows).
 * Does not mutate `baseline`. Schedulings/tariffs without a match are left untouched.
 *
 * Complexity: O(repriced × tariffs) to pre-build lookup maps, then O(baseline × tariffs) for O(1) lookups.
 */
export const overlayRepricedPrices = (baseline: Scheduling[], repriced: Scheduling[]): Scheduling[] => {
  const repricedMaps = new Map<string, PriceMaps>(repriced.map((s) => [s.id, buildPriceMaps(s)]));

  return baseline.map((sch) => {
    const maps = repricedMaps.get(sch.id);
    if (!maps) return sch;

    const { unitPriceByKey, tariffPriceById } = maps;

    return {
      ...sch,
      tariffGroup: {
        ...sch.tariffGroup,
        tariffs: sch.tariffGroup.tariffs.map((t) => {
          const repricedTariff = tariffPriceById.get(t.id);
          return repricedTariff
            ? { ...t, priceCents: repricedTariff.priceCents, oldPriceCents: repricedTariff.oldPriceCents }
            : t;
        }),
      },
      tariffScheduleUnitPrices: sch.tariffScheduleUnitPrices.map((tsup) => ({
        ...tsup,
        tariffs: tsup.tariffs.map((t) => {
          const repricedPriceCents = unitPriceByKey.get(priceKey(tsup.time, t.tariffId));
          return repricedPriceCents !== undefined ? { ...t, priceCents: repricedPriceCents } : t;
        }),
      })),
    };
  });
};
